import React, { useState, useEffect } from 'react';
import { Link , RouteComponentProps } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Typography
} from '@material-ui/core';
import MaterialTable from 'material-table';
import Alert from '@material-ui/lab/Alert';

import AdminLayout from '../../../../../../../layouts/AdminLayout';
import Announcement from '../../../../../../../models/Announcement';
import CourseWork from '../../../../../../../models/CourseWork';
import StudentSubmission from '../../../../../../../models/StudentSubmission';
import { useStudentPerspective } from '../../../../../../../context/StudentPerspective';
import utils from '../../../../../../../libs/utils';

interface StudentCourseWork extends CourseWork {
  submission?: StudentSubmission
}

const Assignments = (props: RouteComponentProps<{ student_id: string, course_id: string }>) => {
  const { student_id: studentId, course_id: courseId } = props.match.params;
  const [courseWorks, setCourseWorks] = useState<StudentCourseWork[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);

  const {
    getCourseById,
    getStudentById,
    hasNewSubmission,
    loadData: loadStudentPerspective
  } = useStudentPerspective();

  const course = getCourseById(courseId);

  const student = getStudentById(studentId);

  const loadCourseWorks = async () => {
    setCourseWorks([]);

    const data = await CourseWork.find({courseId: courseId});
    const submissions = await Promise.all(data.map(a => StudentSubmission.find({
      courseId: a.courseId,
      courseWorkId: a.id
    })));

    setCourseWorks(data.map((courseWork, index) => {
      const studentCourseWork = courseWork as StudentCourseWork;
      studentCourseWork.submission = (() => {
        for (let submission of submissions[index]) {
          if (submission.userId === studentId)
            return submission;
        }
      })();
      return studentCourseWork;
    }).filter(a => a.associatedWithDeveloper));
  };

  const loadAnnouncements = async () => {
    let list = await Announcement.getAnnouncments();
    setAnnouncements(list);
  };

  const isActive = (date: string) => {
    if(new Date().getTimezoneOffset() === -480) {
      return new Date(date) > utils.offsetDateToUS(new Date().toString());
    }
    return new Date(date) > new Date(); // For US TA's
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      await Promise.all([
        loadStudentPerspective(),
        loadCourseWorks(),
        loadAnnouncements()
      ]);
    } catch (e) {
      setErrorMessage(utils.getRequestErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    (async () => await loadData())();
  }, []);

  return (
    <AdminLayout>
      <div className="py-4">
        {(() => {
          if (isLoading)
            return (
              <div className="overlay-loader">
                <div className="text-center p-5">
                  <CircularProgress />
                  <p className="m-0">Loading Assignments</p>
                </div>
              </div>
            );

          if (errorMessage)
            return (
              <div className="text-center p-5">
                <h2 className="m-0">Error Loading Assignments</h2>
                <p>{errorMessage}</p>
                <Button
                  type="button"
                  onClick={loadCourseWorks}
                  color="primary"
                  className="mt-3"
                  variant="contained"
                >
                  Try Again
                </Button>
              </div>
            );

          return (
            <>
              {announcements.map(a =>
                (a.courseID === courseId || a.courseID === '*') && isActive(a.expiration)?
                  <Alert severity="info" color="info" style={{margin: '7px 0 7px 0'}}>
                    {a.message}
                  </Alert>:null
              )}

              <MaterialTable
                style={{minHeight: '60vh'}}
                title={(
                  <Box pt={2}>
                    <Typography variant="h5">Assignments</Typography>
                    <Breadcrumbs aria-label="breadcrumb">
                      <Link to="/admin/students">Students</Link>
                      <Typography>{student?.fullName || studentId}</Typography>
                      <Link to={`/admin/students/${studentId}/courses`}>Courses</Link>
                      <Typography>{course?.fullname || courseId}</Typography>
                      <Typography>Assignments</Typography>
                    </Breadcrumbs>
                  </Box>
                )}
                data={courseWorks}
                options={{
                  search:false,
                  paging: false,
                  headerStyle: {
                    fontWeight: 'bold'
                  },
                  thirdSortClick: false
                }}
                columns={[
                  {
                    title: 'Name',
                    field: 'title',
                    render(courseWork) {
                      return (
                        <Link to={`/admin/courses/${courseId}/assignments/${courseWork.id}/students/${studentId}`}>
                          {courseWork.title}
                        </Link>
                      );
                    }
                  },
                  {
                    title: 'Grade',
                    field: 'submission.assignedGrade',
                    render(courseWork) {
                      if (courseWork.submission && courseWork.submission.assignedGrade)
                        return courseWork.submission.assignedGrade;
                      return 'n/a';
                    }
                  },
                  {
                    title: 'Submitted',
                    sorting: false,
                    render: (courseWork) => {
                      if (courseWork.submission) {
                        if (hasNewSubmission(courseId, studentId, courseWork.id)) {
                          return (
                            <div
                              style={{
                                height: 15,
                                width: 15,
                                borderRadius: 15,
                                backgroundColor: '#2196F3',
                                float: 'right',
                              }}
                            />
                          );
                        }

                        if (Object.keys(courseWork.submission.assignmentSubmission).length) {
                          return '✓';
                        }
                      }
                      return '';
                    }
                  }
                ]}
              />
            </>
          );
        })()}
      </div>
    </AdminLayout>
  );
};

export default Assignments;
